import { ApiResponse } from './api/apiClient';
import BpsRecord from '../interfaces/BpsRecord';
import * as d3 from 'd3';
import { YearReportData } from '../interfaces/RankingsAndLevelsInterfaces';

export function extractData(res: ApiResponse) {
  return res.data;
};

export function compactNumber(n: number, d: number): { n: string, symbol: string} {
  const si = [
    { value: 1, symbol: "" },
    { value: 1E3, symbol: "k" },
    { value: 1E6, symbol: "M" },
    { value: 1E9, symbol: "MM" }
  ];
  let i;
  for (i = si.length - 1; i > 0; i--) {
    if (n >= si[i].value) {
      break;
    }
  }

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const formattedNumber = (n / si[i].value).toFixed(d).replace(rx, "$1");

  return { n: formattedNumber, symbol: si[i].symbol };
};

export function formatNumber(n: number, d: number, lng: string): string {
  const compactNumberValue = parseFloat(compactNumber(n, d)['n']);
  const compactNumberSymbol = compactNumber(n, d)['symbol'];

  return `${ new Intl.NumberFormat(lng).format(compactNumberValue) } ${ compactNumberSymbol }`;
};

export function formatNumberWithSymbol(n: number, d: number, lng: string): { n: string, symbol: string} {
  const compactNumberValue = parseFloat(compactNumber(n, d)['n']);
  const compactNumberSymbol = compactNumber(n, d)['symbol'];

  return {
    n: new Intl.NumberFormat(lng).format(compactNumberValue),
    symbol: compactNumberSymbol
  };
};

const groupBpsSeries = (bpsRecords: BpsRecord[]): Map<string, BpsRecord[]> => {
  return bpsRecords.reduce((rv, x) => {
    let currentRecords = rv.get(x.region) || [];

    rv.set(x.region, currentRecords.concat([x]));

    return rv;
  }, new Map<string, BpsRecord[]>());
};

const groupByYear = (bpsRecords: BpsRecord[]): Map<number, BpsRecord[]> => {
  return bpsRecords.reduce((rv, x) => {
    let currentRecords = rv.get(x.year) || [];

    rv.set(x.year, currentRecords.concat([x]));

    return rv;
  }, new Map<number, BpsRecord[]>());
};

const REGIONS_KEYS = ['G7', 'BRICS', 'RDM', 'REM'];

export function groupForStacked(bpsRecords: BpsRecord[])  {
  const yearGroups = groupByYear(bpsRecords);
  const dataset = Array.from(yearGroups.entries())
    .map((entry) => {
      const yearValues = entry[1].reduce((prev: any, current: BpsRecord) => {
        prev[current.region] = current.bps;

        return prev
      }, {});

      return { year: entry[0], ...yearValues };
    });

  const stack = d3.stack()
      .keys(REGIONS_KEYS)
      .order(d3.stackOrderNone)
      .offset(d3.stackOffsetNone);

  return stack(dataset);
};

// The featured report is the newest year that has a PDF, so a new data year can ship
// before its report is published. Past reports are the five years before it.
export function downloadableReports(reportsAvailables: { [year: number]: YearReportData }) {
  const years = Object.keys(reportsAvailables).map(Number).sort((a, b) => b - a);
  const hasUrls = (year: number) => Object.keys(reportsAvailables[year].download_urls || {}).length > 0;
  const current = years.find(hasUrls) || years[0];
  return {
    thisYearReport: reportsAvailables[current],
    pastReports: years.filter(year => year < current).slice(0, 5).map(year => reportsAvailables[year]),
  };
}
